/**
 * Elite Video Autoplay - Shared functionality for Elite 3D Mask sections
 * Handles video autoplay, iOS compatibility, intersection observer, and preloading
 */

class LPVideoAutoplay {
  constructor(videoSelector, options = {}) {
    this.video = document.querySelector(videoSelector);
    if (!this.video) return;

    this.options = {
      fadeInClass: 'is-visible',
      threshold: 0.5,
      enableFadeIn: true,
      enableConsoleLogs: false,
      enableLoop: false,
      ...options,
    };

    this.src = this.video.currentSrc || this.video.src;
    this.hasPlayed = false;
    this.hasEnded = false;

    this.init();
  }

  init() {
    // iOS video activation
    this.setupIOSActivation();

    // Listen for video end
    this.video.addEventListener('ended', () => {
      this.hasEnded = true;
    });

    // Initialize observer when video metadata is loaded
    this.once(this.video, 'loadedmetadata', () => {
      this.createVideoObserver();
    });

    // Also try to initialize immediately if video is already loaded
    if (this.video.readyState >= 1) {
      this.createVideoObserver();
    }

    // Preload video for better performance (non-iOS)
    this.preloadVideo();
  }

  setupIOSActivation() {
    const activateVideo = () => {
      this.video.play();
      this.video.pause();
    };

    this.once(document.documentElement, 'touchstart', activateVideo);
    this.once(window, 'touchstart', activateVideo);
    this.once(this.video, 'touchstart', activateVideo);
  }

  createVideoObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: this.options.threshold,
    };

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.handleVideoPlay();
        }
      });
    }, options);

    observer.observe(this.video);

    // Immediately check if video is in viewport and try to play
    if (this.isElementInViewport(this.video, this.options.threshold)) {
      // Ensure video is visible immediately
      this.video.style.opacity = '1';
      this.video.classList.add('is-visible');

      // Set opacity immediately if fade-in is enabled
      if (this.options.enableFadeIn) {
        this.video.classList.add(this.options.fadeInClass);
      }
      this.handleVideoPlay();
    }

    // Also try to play immediately when video is ready
    this.once(this.video, 'canplay', () => {
      if (this.isElementInViewport(this.video, this.options.threshold)) {
        this.handleVideoPlay();
      }
    });
  }

  handleVideoPlay() {
    // Always ensure video is visible when it comes into view
    this.video.style.opacity = '1';
    this.video.classList.add('is-visible');

    // Add fade-in class if enabled (for CSS transitions)
    if (this.options.enableFadeIn) {
      this.video.classList.add(this.options.fadeInClass);
    }

    // For looping videos, we don't need to restart them when they end
    if (this.options.enableLoop && this.hasEnded) {
      // Video will loop automatically, just mark as played
      this.hasEnded = false;
      this.hasPlayed = true;
      if (this.options.enableConsoleLogs) {
        console.log('Video will loop automatically');
      }
    } else if (this.hasEnded) {
      this.video.currentTime = 0;
      this.video.play();
      this.hasEnded = false;
      this.hasPlayed = true;
      if (this.options.enableConsoleLogs) {
        console.log('Video restarted on re-entering viewport');
      }
    } else if (!this.hasPlayed || this.video.paused) {
      this.video
        .play()
        .then(() => {
          this.hasPlayed = true;
          if (this.options.enableConsoleLogs) {
            console.log('Video started playing');
          }
        })
        .catch(error => {
          if (this.options.enableConsoleLogs) {
            console.log('Video play failed:', error);
          }
          // Retry after a short delay for better compatibility
          setTimeout(() => {
            this.video.play().catch(e => {
              if (this.options.enableConsoleLogs) {
                console.log('Video retry failed:', e);
              }
            });
          }, 100);
        });
    }
  }

  preloadVideo() {
    setTimeout(() => {
      if (window['fetch'] && !this.isIOS()) {
        fetch(this.src)
          .then(response => response.blob())
          .then(response => {
            const blobURL = URL.createObjectURL(response);
            const t = this.video.currentTime;
            this.once(document.documentElement, 'touchstart', () => {
              this.video.play();
              this.video.pause();
            });
            this.video.setAttribute('src', blobURL);
            this.video.currentTime = t + 0.01;
          });
      }
    }, 1000);
  }

  // Utility functions
  once(el, event, fn, opts) {
    const onceFn = e => {
      el.removeEventListener(event, onceFn);
      fn.apply(this, arguments);
    };
    el.addEventListener(event, onceFn, opts);
    return onceFn;
  }

  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }

  isElementInViewport(el, threshold = 0.5) {
    const rect = el.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    const vertInView =
      rect.top <= windowHeight * (1 - threshold) && rect.top + rect.height * threshold >= 0;
    const horInView = rect.left <= windowWidth && rect.left + rect.width >= 0;
    return vertInView && horInView;
  }
}

// Counter animation functionality
class LPCounterAnimation {
  constructor(counterSelector, options = {}) {
    this.counterElement = document.querySelector(counterSelector);
    if (!this.counterElement) return;

    this.options = {
      threshold: 0.5,
      rootMargin: '0px 0px -50px 0px',
      duration: 2000,
      enableConsoleLogs: false,
      ...options,
    };

    this.targetNumber = parseInt(this.counterElement.dataset.target);
    if (isNaN(this.targetNumber)) return;

    this.hasAnimated = false;

    this.init();
  }

  init() {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.hasAnimated) {
            if (this.options.enableConsoleLogs) {
              console.log(
                'Counter animation triggered:',
                this.counterElement,
                'Target:',
                this.targetNumber,
              );
            }
            this.hasAnimated = true;
            this.animateCounter(this.counterElement, this.targetNumber);
          }
        });
      },
      {
        threshold: this.options.threshold,
        rootMargin: this.options.rootMargin,
      },
    );
    observer.observe(this.counterElement);
  }

  animateCounter(element, target) {
    const duration = this.options.duration;
    const startTime = performance.now();

    const updateCounter = currentTime => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(target * easeOutQuart);
      element.textContent = currentValue.toLocaleString();

      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      } else {
        element.textContent = target.toLocaleString();
      }
    };

    requestAnimationFrame(updateCounter);
  }
}

// Fade-up animation functionality
class LPFadeUpAnimation {
  constructor(elementSelector, options = {}) {
    this.elements = document.querySelectorAll(elementSelector);
    if (this.elements.length === 0) return;

    this.options = {
      threshold: 0.2,
      rootMargin: '0px 0px -50px 0px',
      duration: 800,
      delay: 0,
      stagger: 100,
      enableConsoleLogs: false,
      ...options,
    };

    this.init();
  }

  init() {
    // Set initial state for all elements
    this.elements.forEach((element, index) => {
      element.style.opacity = '0';
      element.style.transform = 'translateY(20px)';
      element.style.transition = `opacity ${this.options.duration}ms ease-out ${
        this.options.delay + index * this.options.stagger
      }ms, transform ${this.options.duration}ms ease-out ${
        this.options.delay + index * this.options.stagger
      }ms`;
    });

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (this.options.enableConsoleLogs) {
              console.log('Fade-up animation triggered:', entry.target);
            }
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          } else {
            // Reset the element when leaving the viewport
            entry.target.style.opacity = '0';
            entry.target.style.transform = 'translateY(20px)';
            if (this.options.enableConsoleLogs) {
              console.log('Fade-up animation reset:', entry.target);
            }
          }
        });
      },
      {
        threshold: this.options.threshold,
        rootMargin: this.options.rootMargin,
      },
    );

    // Observe each element individually
    this.elements.forEach(element => {
      observer.observe(element);
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  // Initialize video autoplay for sections that need it
  const videoSections = [
    {
      selector: '.elite-3d-mask-1-background',
      options: {enableFadeIn: true, enableConsoleLogs: true},
    },
    {selector: '.elite-3d-mask-3__video', options: {enableFadeIn: false}},
    {selector: '.elite-3d-mask-2__video', options: {enableFadeIn: false}},
    {selector: '.elite-skin__video', options: {enableFadeIn: false}},
    {selector: '.lp-video-scrub-video-bg', options: {enableFadeIn: false}},
    {
      selector: '.elite-3d-mask-4-background',
      options: {enableFadeIn: true, enableConsoleLogs: true, enableLoop: true},
    },
  ];

  videoSections.forEach(section => {
    new LPVideoAutoplay(section.selector, section.options);
  });

  // Initialize counter animations
  const counterSections = [
    {selector: '.stats-columns__stat-1 .stats-columns__number-target'},
    {selector: '.stats-columns__stat-2 .stats-columns__number-target'},
    {selector: '.stats-columns__stat-4 .stats-columns__number-target'},
    {selector: '.results-percentage-number-1'},
    {selector: '.results-percentage-number-2'},
    {selector: '.results-percentage-number-3'},
  ];

  counterSections.forEach(section => {
    new LPCounterAnimation(section.selector, section.options);
  });

  // Initialize fade-up animations
  const fadeUpSections = [
    {selector: '.lp-video-loop-scroll-heading'},
    {selector: '.lp-video-loop-description', options: {delay: 200}},
    {selector: '.lp-video-loop__button', options: {delay: 400}},
    {selector: '.lp-video-background-left__title'},
    {selector: '.lp-video-background-left__text-title', options: {delay: 200}},
    {selector: '.lp-video-background-left__description', options: {delay: 400}},
    {selector: '.lp-video-background-left__button', options: {delay: 600}},
    {selector: '.lp-video-trigger-scroll__title'},
    {selector: '.lp-video-trigger-scroll__description', options: {delay: 200}},
    {selector: '.lp-video-trigger-scroll__button', options: {delay: 600}},
    {selector: '.checklist-title', options: {delay: 0}},
    {selector: '.lp-images-slide-left-right__heading'},
    {selector: '.lp-images-slide-left-right__description', options: {delay: 200}},
    {selector: '.lp-video-scrub-title'},
    {selector: '.lp-video-scrub-subtitle', options: {delay: 200}},
    {selector: '.lp-video-scrub-cta-button', options: {delay: 600}},
    {selector: '.founder-words-title'},
    {selector: '.founder-words-text', options: {delay: 200}},
    {selector: '.founder-words-author', options: {delay: 400}},
    {selector: '.lp-results-headline *:first-child'},
    {selector: '.lp-results-headline *:nth-child(2)', options: {delay: 200}},
    {selector: '.lp-results-description', options: {delay: 400}},
    {selector: '.lp-results-percentage-row-1', options: {delay: 400}},
    {selector: '.lp-results-percentage-row-2', options: {delay: 500}},
    {selector: '.lp-results-percentage-row-3', options: {delay: 600}},
    {selector: '.lp-results-percentage-row-4', options: {delay: 600}},
    {
      selector: '.checklist-column:nth-child(1) .checklist-item:nth-child(1)',
      options: {delay: 100},
    },
    {
      selector: '.checklist-column:nth-child(1) .checklist-item:nth-child(2)',
      options: {delay: 300},
    },
    {
      selector: '.checklist-column:nth-child(1) .checklist-item:nth-child(3)',
      options: {delay: 500},
    },
    {
      selector: '.checklist-column:nth-child(2) .checklist-item:nth-child(1)',
      options: {delay: 700},
    },
    {
      selector: '.checklist-column:nth-child(2) .checklist-item:nth-child(2)',
      options: {delay: 500},
    },
    {
      selector: '.checklist-column:nth-child(2) .checklist-item:nth-child(3)',
      options: {delay: 600},
    },
  ];

  fadeUpSections.forEach(section => {
    new LPFadeUpAnimation(section.selector, section.options);
  });
});
